import { Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Sku, SkuVatSuggestion } from 'src/app/common/Sku';
import { ProductCategory, productCategoryLabel } from 'src/app/common/ProductCategory';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { SkuService } from 'src/app/services/sku.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule, Location } from '@angular/common';
import { Vat } from 'src/app/common/Vat';
import { vatLabel } from 'src/app/common/vat-label';
import { VatService } from 'src/app/services/vat.service';
import { FileSelectComponent } from '@progress/kendo-angular-upload';
import { ProductIdentificationResponse } from 'src/app/common/IdentifyProductResponse';
import { SkuPostModel } from 'src/app/common/SkuPostModel';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ProductValidatorService } from '../../services/product-validator.service';
import { AppProfileService } from '../../services/profile.service';
import { ExtendedProfile } from '../../common/ExtendedProfile';
import { InvoiceTypeConstants } from '../../common/InvoiceType';
import { FilePostDto } from '../../common/FilePostDto';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { ScanComponent } from '../scan/scan.component';
import { FilesComponent } from '../files/file-select.component';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { EMPTY, forkJoin, Observable, of, switchMap, take, tap } from 'rxjs';
import { FormRowComponent } from '../form-row/form-row.component';

/** The article form; the conditional rules ask `when` at validation time. Its type is inferred from here (#199). */
function buildProductForm(fb: FormBuilder,
  sku: SkuPostModel,
  validators: ProductValidatorService,
  when: { retailer: () => boolean; gross: () => boolean; vatRequired: () => boolean },
) {
  return fb.group({
    number: [
      sku?.number,
      [
        // Retailers must supply an explicit product number; non-retailers may leave it
        // empty (the backend auto-generates a GUID in SkuCreateCommand).
        validators.conditionalValidator(
          when.retailer,
          Validators.required
        ),
        validators.conditionalValidator(
          when.retailer,
          Validators.maxLength(50)
        ),
      ],
    ],
    description: [
      sku?.description,
      Validators.maxLength(10000),
    ],
    shortName: [
      sku?.shortName,
      [
        validators.conditionalValidator(
          when.retailer,
          Validators.maxLength(100)
        ),
        validators.conditionalValidator(
          when.retailer,
          Validators.maxLength(100)
        ),
      ],
    ],
    // #633 A: the 0..n tag set — deliberately NOT required (min-0: an uncategorized
    // listing is a legal state, findable via text search only).
    productCategoryIds: [sku?.productCategoryIds ?? []],
    availableQuantity: [sku?.availableQuantity, Validators.required],
    name1: [
      sku?.name1,
      // Mirrors backend SkuConstraints.Name1MaxLength (Sku.Name1 nvarchar(255)) —
      // change them together.
      [Validators.required, Validators.maxLength(255)],
    ],
    catalogNetPrice: [
      sku?.catalogNetPrice,
      validators.conditionalValidator(
        () => !when.gross(),
        Validators.required
      ),
    ],
    catalogGrossPrice: [
      sku?.catalogGrossPrice,
      validators.conditionalValidator(
        when.gross,
        Validators.required
      ),
    ],
    vatId: [
      sku?.vatId,
      validators.conditionalValidator(
        when.vatRequired,
        Validators.required
      ),
    ],
    isActive: [sku?.isActive],
  });
}
type ProductForm = ReturnType<typeof buildProductForm>;

@Component({
    imports: [CommonModule, ReactiveFormsModule, KENDO_DROPDOWNS, ScanComponent, FilesComponent, KENDO_ICONS, AsyncClickDirective, FormRowComponent],
    selector: 'app-sku-edit',
    templateUrl: './sku-edit.component.html',
    styleUrls: ['./sku-edit.component.css']
})
export class SkuEditComponent implements OnInit {
  fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  skuService = inject(SkuService);
  private productCategoryService = inject(ProductCategoryService);
  private location = inject(Location);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private vatService = inject(VatService);
  private productValidatorService = inject(ProductValidatorService);
  private profileService = inject(AppProfileService);
  private apiActionService = inject(CallApiService);

  readonly fileSelect = viewChild<FileSelectComponent>('fileselect');

  public id: string;
  public productForm: ProductForm;
  public submitted = false;
  public productCategories: ProductCategory[];
  public vats: Vat[];
  public profile: ExtendedProfile;
  public sku: SkuPostModel;
  public fileTransactions: FilePostDto[] = [];
  public filesChanged: boolean;
  public faTrashAlt = faTrashAlt;
  public isGrossInvoiceType: boolean = false;
  public isVatRequired: boolean = false;
  // #521 (A6): true when editing a scan-imported listing. The scan-owned fields (name1,
  // description, prices) are shown read-only — the next scan-sync overwrites manual edits.
  public isImported: boolean = false;
  // Backend #579 (slice 1): true while the listing carries the undetermined VAT sentinel —
  // unsellable until the seller picks a rate. The dropdown never offers the sentinel, so the
  // control shows no selection; the hint tells the seller why and what to do.
  public isVatUndetermined: boolean = false;
  // Backend #579 (slice 2): the chain's pending VAT suggestion — accepted or dismissed here.
  public vatSuggestion: SkuVatSuggestion | null = null;
  // Accept and dismiss are mutually exclusive mutations on the same suggestion.
  public readonly vatSuggestionLock = { busy: false };

  // #521 (A6): the scan-owned controls the sku-edit form actually binds. GTIN/msrpNetPrice/
  // hasSerial are also scan-owned but not editable in this form, so nothing to lock there.
  private readonly scanOwnedControls = ['name1', 'description', 'catalogNetPrice', 'catalogGrossPrice'];

  get f() {
    return this.productForm.controls;
  }

  private destroyRef = inject(DestroyRef);

  public ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    this.productCategories = [];
    this.vats = [];
    this.sku = new SkuPostModel();
    this.sku.isActive = true;

    // The profile once (#199): a profile refresh while editing used to re-fetch everything and
    // rebuild the form, discarding the edits. The three loads are independent and run together.
    this.profileService.currentProfile$
      .pipe(
        take(1),
        switchMap((p) => {
          this.profile = p;
          this.isGrossInvoiceType = this.profile.customer.invoiceType == InvoiceTypeConstants.gross;
          this.isVatRequired = !this.profile.customer.smallBusinessP19UStG
            && (!this.isGrossInvoiceType || this.profile.customer.isRetailer);
          if (!this.id) {
            this.productForm = this.createForm(this.sku);
          }
          return forkJoin({
            categories: this.productCategoryService.get(),
            vats: this.vatService.get(),
            sku: this.id ? this.skuService.getById(this.id) : of<Sku>(null),
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ categories, vats, sku }) => {
        this.productCategories = categories;
        this.vats = vats;
        if (sku) {
          this.applyFromSku(sku);
        }
      });
  }

  /**
   * #178: the VAT dropdown's items. Kendo's `textField` reads a property, so the label has to
   * exist as one — it used to point at `percentage`, which rendered a bare „19".
   */
  public get vatItems(): { id: string; label: string }[] {
    return (this.vats ?? []).map((v) => ({ id: v.id, label: vatLabel(v) }));
  }

  // #633: a set has no order but the UI shows one — keep the chips alphabetical by
  // category name, so two listings with the same tags always look identical.
  // #178: sorted by the RESOLVED name, so the order follows the words on screen rather than
  // the ASCII keys behind them.
  public onCategoriesChange(selected: string[]) {
    const byName = (id: string) =>
      productCategoryLabel(this.productCategories.find((c) => c.id === id)) || id;
    const sorted = [...(selected ?? [])].sort((a, b) => byName(a).localeCompare(byName(b)));
    if (sorted.some((id, i) => id !== selected[i])) {
      this.productForm.controls.productCategoryIds.setValue(sorted, { emitEvent: false });
    }
  }

  public submitForm(): Observable<unknown> {
    if (!this.productForm.valid) {
      return EMPTY;
    }
    this.sku.number = this.productForm.controls.number.value;
    this.sku.description = this.productForm.controls.description.value;
    this.sku.shortName = this.productForm.controls.shortName.value;
    this.sku.productCategoryIds = this.productForm.controls.productCategoryIds.value ?? [];
    this.sku.name1 = this.productForm.controls.name1.value;
    this.sku.catalogNetPrice = this.productForm.controls.catalogNetPrice.value;
    this.sku.catalogGrossPrice = this.productForm.controls.catalogGrossPrice.value;
    this.sku.vatId = this.productForm.controls.vatId.value;
    this.sku.isActive = this.productForm.controls.isActive.value ?? false;
    this.sku.availableQuantity = this.productForm.controls.availableQuantity.value;
    this.submitted = true;

    return (this.sku.id ? this.update() : this.create()).pipe(
      tap((item: Sku) => {
        this.sku.id = item.id;
        this.filesChanged = false;
        // Forward-navigate to the SKU list rather than history.back(): Firefox
        // restores the prior page via bfcache on history.back(), which discards
        // ngx-toastr's just-inserted container and the success toast with it.
        this.router.navigateByUrl('/sku-list');
      })
    );
  }

  public applyFromIdentificationResponse(response: ProductIdentificationResponse) {
    if (response.isSuccess) {
      if (!this.id && response.gtin) {
        if (response.sku) {
          this.sku = {
            ...response.sku,
            gtin: response.gtin,
            availableQuantity: this.sku.availableQuantity,
            isActive: true,
          };
        }

        this.toastr.success(
          $localize`Produkt wurde identifiziert. Ergänzen Sie die Daten, um den Artikel zu speichern`
        );

        this.productForm = this.createForm(this.sku);
      }
    } else {
      this.toastr.warning($localize`Leider konnte kein Produkt mit diesem Code ermittelt werden.`);
    }
  }

  public fileAdded(_filePostDto: FilePostDto) {
    this.filesChanged = true;
  }

  public fileRemoved(_filePostDto: FilePostDto) {
    this.filesChanged = true;
  }

  public onReset(): void {
    this.submitted = false;
    this.productForm.reset();
    this.filesChanged = false;
  }

  public onBackClicked() {
    this.location.back();
  }

  private createForm(sku: SkuPostModel): ProductForm {
    return buildProductForm(this.fb, sku, this.productValidatorService, {
      retailer: () => this.profile?.customer?.isRetailer,
      gross: () => this.isGrossInvoiceType,
      vatRequired: () => this.isVatRequired,
    });
  }

  // create()/update() return the toast-piped stream; the shared success handling
  // (set id, reset filesChanged, navigate) lives in submitForm's tap().
  private create(): Observable<Sku> {
    return this.apiActionService.callWithToasts$(
      this.skuService.createMultipart(this.sku, this.fileTransactions),
      $localize`Artikel wurde angelegt.`,
      $localize`Beim Anlegen des Artikels ist ein Fehler aufgetreten.`,
      this.productForm
    );
  }

  private update(): Observable<Sku> {
    return this.apiActionService.callWithToasts$(
      this.skuService.updateMultipart(this.sku.id, this.sku, this.fileTransactions),
      $localize`Artikel wurde aktualisiert.`,
      $localize`Beim Aktualisieren des Artikels ist ein Fehler aufgetreten.`,
      this.productForm
    );
  }

  // Backend #579 (slice 2): the seller's decision on the pending VAT suggestion. Both return
  // the updated listing, which re-seeds the form (accept: new rate + re-derived prices; both:
  // suggestion gone). Bound via [asyncClick] with a shared lock, so a double click or an
  // accept-while-dismissing cannot fire twice.
  acceptVatSuggestion(): Observable<Sku> {
    return this.apiActionService
      .callWithToasts$(
        this.skuService.acceptVatSuggestion(this.id),
        $localize`Vorgeschlagener MwSt.-Satz wurde übernommen.`,
        $localize`Der Vorschlag konnte nicht übernommen werden.`
      )
      .pipe(tap((sku) => this.applyFromSku(sku)));
  }

  dismissVatSuggestion(): Observable<Sku> {
    return this.apiActionService
      .callWithToasts$(
        this.skuService.dismissVatSuggestion(this.id),
        $localize`Vorschlag wurde verworfen.`,
        $localize`Der Vorschlag konnte nicht verworfen werden.`
      )
      .pipe(tap((sku) => this.applyFromSku(sku)));
  }

  // Display name of the resolver that made the suggestion — the wire carries the registry key.
  // #178: a key this build does not know used to be printed raw, so a new resolver would put
  // „ai-someprovider" in front of the seller in both languages. It is named as another source
  // instead, with the key kept in the sentence because it is the only handle support has when
  // a seller asks where a suggestion came from.
  vatSuggestionSourceLabel(source: string | undefined): string {
    switch (source) {
      case 'ai-anthropic':
        return $localize`KI`;
      case 'ruling':
        return $localize`Regelwerk`;
      case undefined:
      case null:
      case '':
        return $localize`unbekannte Quelle`;
      default:
        return $localize`andere Quelle: ${source}:sourceKey:`;
    }
  }

  private applyFromSku(sku: Sku) {
    if (this.id) {
      this.sku = {
        ...sku,
        gtin: '',
        availableQuantity: sku.availableQuantity,
        isActive: true,
      };

      this.fileTransactions = (sku.files ?? []).map((f) => ({
        filePayload: f,
        isCreated: false,
        isDeleted: false,
      }));

      this.productForm = this.createForm(this.sku);

      // #521 (A6): lock the scan-owned fields for imported listings. Disabled controls still
      // surface their value via get(name).value in submitForm, so the loaded scan values
      // round-trip unchanged instead of being cleared. Seller-owned fields stay editable.
      this.isImported = sku.isImported === true;
      if (this.isImported) {
        this.scanOwnedControls.forEach((name) => this.productForm.get(name)?.disable());
      }

      // Backend #579: surface the undetermined-VAT state; the sentinel id is not in `vats`
      // (the BFF withholds it), so the dropdown renders empty until the seller chooses.
      this.isVatUndetermined = sku.isVatUndetermined === true;

      // Backend #579 (slice 2): the pending suggestion (null/missing = none, also on older backends).
      this.vatSuggestion = sku.vatSuggestion ?? null;
    }
  }
}
